import { IStudioSelectItemModel } from "../infrastructure/models/studioSelectItemModel";
import { formatEurValue } from "../utilities/currencyUtility";

/**
 * Formats a price for the {{price}} placeholder. Event/service prices are stored
 * in EUR, so the placeholder always carries the euro symbol — an amount without a
 * currency is meaningless in a consent form the user is legally agreeing to.
 * A missing price renders empty rather than "€ 0.00", which would read as "free".
 *
 * A service priced as a band renders the band verbatim ("€ 20.00 - € 35.00"), the
 * same shape the booking steps show. `maxPrice` is ignored when it matches or
 * undercuts `minPrice`, so a single-price target never renders a degenerate range.
 */
export const formatFormPrice = (
  minPrice?: number | null,
  maxPrice?: number | null,
): string => {
  if (minPrice === undefined || minPrice === null) {
    return "";
  }

  const min = formatEurValue(minPrice);

  if (maxPrice === undefined || maxPrice === null || maxPrice <= minPrice) {
    return min;
  }

  return `${min} - ${formatEurValue(maxPrice)}`;
};

const formatFormDate = (date?: Date | string | null): string => {
  if (!date) {
    return "";
  }
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${parsed.getFullYear()}`;
};

interface IFormEventContextSource {
  name?: string;
  price?: number | null;
  /** A service's price band, when it has one. Falls back to `price` when absent. */
  serviceMinPrice?: number | null;
  serviceMaxPrice?: number | null;
  date?: Date | string | null;
  startTime?: string;
  durationInMinutes?: number | null;
  instructor?: string;
  hallName?: string;
}

/**
 * Maps an event-shaped object onto the context placeholders it can answer. Fields
 * the caller cannot supply are simply left out, so `buildClientFormContext` falls
 * back to "" for them.
 */
export const buildEventFormContext = (
  event?: IFormEventContextSource,
): Record<string, string> => {
  if (!event) {
    return {};
  }

  const date = formatFormDate(event.date);
  const time = event.startTime ?? "";

  // A service event snapshots its band's top as Event.Price, so prefer the band
  // itself when the event carries one — otherwise the form would quote the ceiling
  // as if it were a fixed amount.
  const hasBand =
    event.serviceMinPrice !== undefined && event.serviceMinPrice !== null;

  return {
    eventName: event.name ?? "",
    price: hasBand
      ? formatFormPrice(event.serviceMinPrice, event.serviceMaxPrice)
      : formatFormPrice(event.price),
    date,
    time,
    datetime: [date, time].filter(Boolean).join(" "),
    duration: event.durationInMinutes ? `${event.durationInMinutes} мин.` : "",
    instructorName: event.instructor ?? "",
    hallName: event.hallName ?? "",
  };
};

/**
 * Best-effort client-side resolution of context placeholders for DISPLAY only.
 * The authoritative snapshot is rendered server-side at reserve time; here we
 * fill in what the portal already knows (studio + user + any event fields the
 * caller passes via `extra`) so the user sees a readable form.
 */
export const buildClientFormContext = (
  selectedStudio?: IStudioSelectItemModel,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentUser?: any,
  extra?: Record<string, string>,
): Record<string, string> => {
  const fullName = `${currentUser?.firstName ?? ""} ${
    currentUser?.lastName ?? ""
  }`.trim();

  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const currentDate = `${dd}.${mm}.${today.getFullYear()}`;

  return {
    studioName: selectedStudio?.name ?? "",
    studioAddress: selectedStudio?.address ?? "",
    studioPhone: selectedStudio?.phoneNumber ?? "",
    studioEmail: selectedStudio?.email ?? "",
    userName: fullName,
    userFirstName: currentUser?.firstName ?? "",
    userLastName: currentUser?.lastName ?? "",
    userPhoneNumber: currentUser?.phoneNumber ?? "",
    userEmail: currentUser?.email ?? "",
    currentDate,
    ...(extra ?? {}),
  };
};
